import type { ApiClient } from "../api/ApiClient";
import { ApiError } from "../api/errors";
import {
  attributionBatchResponseSchema,
  clockStateResponseSchema,
  eventBatchResponseSchema,
  myProjectsSchema,
  meSchema,
  projectDaysSchema,
  serverTimeSchema,
  workDaysSchema,
} from "../api/schemas";
import type {
  AttributionBatchResponse,
  AttributionRequestBody,
  ClockStateResponse,
  EventBatchResponse,
  EventRequestBody,
  MyProjects,
  ProjectDay,
  WorkDayResponse,
} from "../api/schemas";
import type { LocalDate } from "../domain/time";
import { hasAccessRight } from "../shared/auth/accessRights";

/**
 * Server operations the synchronization needs (port; spec 8.1, 10).
 */
export interface SyncApi {
  /** Checks mapped roles before time synchronization; omitted by adapters with fixed record access. */
  canAccessOwnRecords?(): Promise<boolean>;
  /** Submits events (`POST /events:batch`). */
  submitBatch(events: readonly EventRequestBody[]): Promise<EventBatchResponse>;
  /** Reads the server time (`GET /time`) as ISO instant. */
  serverTime(): Promise<string>;
  /** Reads the clock state (`GET /me/clock-state`). */
  clockState(): Promise<ClockStateResponse>;
  /** Reads working days (`GET /me/workdays`). */
  workDays(from: LocalDate, to: LocalDate): Promise<readonly WorkDayResponse[]>;
  /** Submits project attributions (`POST /project-attributions:batch`, T-124). */
  submitAttributions(
    attributions: readonly AttributionRequestBody[],
  ): Promise<AttributionBatchResponse>;
  /** Reads the selectable projects and the requirement (`GET /me/projects`). */
  myProjects(): Promise<MyProjects>;
  /** Reads the project view of working days (`GET /me/project-days`); empty if not available. */
  projectDays(from: LocalDate, to: LocalDate): Promise<readonly ProjectDay[]>;
}

/** Projects of a server without project time tracking: nothing to choose. */
const noProjects: MyProjects = {
  requirement: {
    mode: "Disabled",
    allocationDeadlineWorkingDays: 3,
    source: "NotAvailable",
    scopeKind: null,
  },
  projects: [],
};

/**
 * Treats "not found" as "not available", so that a PWA that is newer than its server still
 * synchronizes clock events (the project endpoints came with T-121 and T-122).
 *
 * @param request - The request.
 * @param fallback - Value when the server does not know the endpoint.
 * @returns The response or the fallback.
 */
async function orIfUnknown<T>(request: Promise<T>, fallback: T): Promise<T> {
  try {
    const response = await request;
    return response;
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return fallback;
    }

    throw error;
  }
}

/**
 * Creates the {@link SyncApi} on top of the API client.
 *
 * @param client - The API client.
 * @returns The adapter.
 */
export function createHttpSyncApi(client: ApiClient): SyncApi {
  const api: SyncApi = {
    canAccessOwnRecords: async () => {
      const identity = await client.get("/api/v1/me", meSchema.pick({ roles: true }));
      const allowed = hasAccessRight(identity.roles, "AccessOwnRecords");
      return allowed;
    },
    submitBatch: (events) =>
      client.post("/api/v1/events:batch", { events }, eventBatchResponseSchema),
    serverTime: async () => {
      const response = await client.get("/api/v1/time", serverTimeSchema, { anonymous: true });
      return response.serverTime;
    },
    clockState: () => client.get("/api/v1/me/clock-state", clockStateResponseSchema),
    workDays: async (from, to) => {
      const response = await client.get("/api/v1/me/workdays", workDaysSchema, {
        query: { from, to },
      });
      return response.days;
    },
    submitAttributions: (attributions) =>
      client.post(
        "/api/v1/project-attributions:batch",
        { attributions },
        attributionBatchResponseSchema,
      ),
    myProjects: () => orIfUnknown(client.get("/api/v1/me/projects", myProjectsSchema), noProjects),
    projectDays: async (from, to) => {
      const response = await orIfUnknown(
        client.get("/api/v1/me/project-days", projectDaysSchema, { query: { from, to } }),
        { isAvailable: false, days: [] },
      );
      const days = response.isAvailable ? response.days : [];
      return days;
    },
  };

  return api;
}
